import fp from 'lodash/fp';

import actionFactory from './actions';

let reducers = {};

const rootReducer = (state = {}, action) => {
    let reducedState = state;
    fp.forEach((reducer) => {
        let reducerBranch = reducedState[reducer.id];
        reducerBranch = reducer(reducerBranch, action);
        if (reducerBranch !== reducedState[reducer.id]) {
            reducedState = fp.set(reducer.id, reducerBranch, reducedState);
        }
    })(reducers);

    return reducedState;
};

const INITIAL_STATE_BASE = {
    filterDefinitions: null,
    filters: [],
    defaultFilters: [],
    currentlyOpenFilter: null,
    currentlyOpenDefaultFilter: null
};

const buildReducer = (id) => {
    if (reducers[id]) {
        return;
    }

    const actions = actionFactory(id);
    const INITIAL_STATE = fp.clone(INITIAL_STATE_BASE);
    const reducer = (state = INITIAL_STATE, action = {}) => {
        switch (action.type) {
            case actions.OPEN_FILTER:
                return fp.set('currentlyOpenFilter', action.filterId, state);
            case actions.OPEN_DEFAULT_FILTER:
                return fp.set('currentlyOpenDefaultFilter', action.defaultFilterId, state);
            case actions.SET_FILTERS:
                return fp.flow(
                    fp.set('currentlyOpenFilter', null),
                    state.filterDefinitions
                        ? fp.set('filters', fp.map((filter) => {
                            const newFilter = fp.find(
                                (f) => f.definition.id === filter.definition.id,
                                action.filters
                            );
                            if (newFilter) {
                                return fp.set('value', newFilter.value, filter);
                            }
                            return fp.set('value', undefined, filter);
                        })(state.filters))
                        : fp.set('filters', action.filters)
                )(state);
            case actions.SET_DEFAULT_FILTERS:
            {
                if (state.filterDefinitions) {
                    return fp.set('defaultFilters', fp.map((defaultFilter) => {
                        const newDefaultFilter = fp.find(
                            (df) => df.definition.id === defaultFilter.definition.id,
                            action.defaultFilters
                        );
                        if (newDefaultFilter) {
                            return fp.set('value', newDefaultFilter.value, defaultFilter);
                        }
                        return fp.set('value', undefined, defaultFilter);
                    })(state.defaultFilters))(state);
                }
                return fp.set('defaultFilters', action.defaultFilters)(state);
            }
            case actions.RESET_FILTERS: {
                return fp.flow(
                    fp.set('currentlyOpenFilter', null),
                    fp.set('filters', state.defaultFilters)
                )(state);
            }
            case actions.REMOVE_FILTER: {
                const index = state.filters.findIndex(
                    (filter) => filter.definition.id === action.filterId
                );
                return index !== -1 ? fp.flow(
                    fp.set(`filters[${index}].value`, undefined),
                    fp.set('currentlyOpenFilter', null)
                )(state) : state;
            }
            case actions.REMOVE_DEFAULT_FILTER: {
                const index = state.defaultFilters.findIndex(
                    (defaultFilter) => defaultFilter.definition.id === action.defaultFilterId
                );
                return index !== -1 ? fp.flow(
                    fp.set(`defaultFilters[${index}].value`, undefined),
                    fp.set('currentlyOpenDefaultFilter', null)
                )(state) : state;
            }
            case actions.UPDATE_FILTER:
            {
                const index = state.filters.findIndex(
                    (filter) => filter.definition.id === action.filterId
                );
                return index !== -1 ? fp.flow(
                    fp.set(`filters[${index}].value`, action.value),
                    fp.set('currentlyOpenFilter', null)
                )(state) : state;
            }
            case actions.UPDATE_DEFAULT_FILTER:
            {
                const index = state.defaultFilters.findIndex(
                    (defaultFilter) => defaultFilter.definition.id === action.defaultFilterId
                );
                return index !== -1 ? fp.flow(
                    fp.set(`defaultFilters[${index}].value`, action.value),
                    fp.set('currentlyOpenDefaultFilter', null)
                )(state) : state;
            }
            case actions.ADD_FILTER:
            {
                const index = state.filters.findIndex(
                    (filter) => filter.definition.id === action.filterId
                );
                return index !== -1 ? fp.flow(
                    fp.set(`filters[${index}].value`, fp.get(`filters[${index}].definition.defaultValue`, state)),
                    fp.set('currentlyOpenFilter', action.filterId)
                )(state) : state;
            }
            case actions.ADD_DEFAULT_FILTER: {
                const index = state.defaultFilters.findIndex(
                    (defaultFilter) => defaultFilter.definition.id === action.defaultFilterId
                );
                return index !== -1 ? fp.flow(
                    fp.set(`defaultFilters[${index}].value`, fp.get(`defaultFilters[${index}].definition.defaultValue`, state)),
                    fp.set('currentlyOpenDefaultFilter', action.defaultFilterId)
                )(state) : state;
            }
            case actions.SET_FILTER_DEFINITIONS:
                // only filters in the filter definitions are allowed
                // all others will be removed
                // merge the value of any existing filter or default
                return fp.flow(
                    fp.set('filterDefinitions', action.filterDefinitions),
                    fp.set('filters',
                        fp.map((filterDefinition) => {
                            const a = {
                                value: fp.get('value', fp.find((filter) => filter.definition.id === filterDefinition.id)(state.filters)),
                                definition: filterDefinition
                            };
                            return a;
                        })(action.filterDefinitions)),
                    fp.set('defaultFilters',
                        fp.map((filterDefinition) => {
                            const a = {
                                value: fp.get('value', fp.find((defaultFilter) => defaultFilter.definition.id === filterDefinition.id)(state.defaultFilters)),
                                definition: filterDefinition
                            };
                            return a;
                        })(action.filterDefinitions))
                )(state);
            case actions.SET_AVAILABLE_LIST_VALUES: {
                let returnState = state;
                let index = state.filters.findIndex(
                    (filter) => filter.definition.id === action.filterId
                );
                if (index !== -1) {
                    returnState = fp.set(`filters[${index}].definition.availableValues`, action.availableValues, returnState);
                }

                index = state.defaultFilters.findIndex(
                    (defaultFilter) => defaultFilter.definition.id === action.filterId
                );
                if (index !== -1) {
                    returnState = fp.set(`defaultFilters[${index}].definition.availableValues`, action.availableValues, returnState);
                }
                return returnState;
            }
            default:
                return state;
        }
    };

    reducer.INITIAL_STATE = INITIAL_STATE;
    reducer.id = id;

    reducers[id] = reducer;
};

const getReducer = (id) => reducers[id];

const clearReducers = () => { reducers = {}; };

export default rootReducer;
export { buildReducer, getReducer, clearReducers };
